import { CardContent, Grid } from '@mui/material'
import React from 'react'
import { type ConnectedProps, connect } from 'react-redux'
import { type AllTenantsState } from '../../redux/getAllTenantsSlice'
import { type getFilterTypesForDeviceKeyMappingsState } from '../../redux/getFilterTypesForKeyDeviceMappingsSlice'
import { type HybridKeksLabelsState } from '../../redux/getHybridKeksLabelsSlice'
import { fetchFilterTypesForKeyDeviceMappings, fetchHybridKeksLabels, fetchTenants } from '../../redux/actionCreator'
import { type RootState } from '../../../../store'
import { HandleError } from '../handleError'
import LanguageStrings from '../../../../i18n/locales'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { KeyTypeDropdown } from '../../../../components/KeyTypeDropdown'

const KeyTypeMappings = LanguageStrings().HSMConfiguration.KeyTypeMappings

interface ParentProps {
  tenant: Option | null
  setTenant: React.Dispatch<React.SetStateAction<Option | null>>
  keyType: Option | null
  setKeyType: React.Dispatch<React.SetStateAction<Option | null>>
  labelOfHybridKek: Option | null
  setLabelOfHybridKek: React.Dispatch<React.SetStateAction<Option | null>>
  error: boolean
  setError: React.Dispatch<React.SetStateAction<boolean>>
  nameErrorInit: boolean
  setNameErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  labelOfHybridKekOptions: Option[]
  setLabelOfHybridKekOptions: React.Dispatch<React.SetStateAction<Option[]>>
  tenantOptions: Option[]
  setTenantOptions: React.Dispatch<React.SetStateAction<Option[]>>
  keyTypeOptions: Option[]
  setKeyTypeOptions: React.Dispatch<React.SetStateAction<Option[]>>
}

const KeyTypeMappingInformation = (props: AllProps): JSX.Element => {
  React.useEffect(() => {
    if (!(props.keyTypes.data !== null && props.keyTypes.data.length > 0)) {
      props.fetchFilterTypesForKeyDeviceMappings()
    }
  }, [])

  React.useEffect(() => {
    if (props.keyTypes.data !== null && props.keyTypes.data.length > 0) {
      const keyTypes: Option[] = []
      props.keyTypes.data.forEach((keyType: { toString: () => any }) => {
        keyTypes.push({
          value: keyType.toString(),
          label: keyType.toString()
        })
      })
      props.setKeyTypeOptions(keyTypes)
    }
  }, [props.keyTypes.data])

  React.useEffect(() => {
    if (!(props.tenantTypes.tenants !== null && props.tenantTypes.tenants.length > 0)) {
      props.fetchTenants()
    }
  }, [])

  React.useEffect(() => {
    if (props.tenantTypes.tenants !== null && props.tenantTypes.tenants.length > 0) {
      const tenants: Option[] = []
      props.tenantTypes.tenants.forEach((tenant) => {
        tenants.push({
          value: tenant.toString(),
          label: tenant.toString()
        })
      })
      props.setTenantOptions(tenants)
    }
  }, [props.tenantTypes.tenants])

  React.useEffect(() => {
    if (props.tenant !== null) {
      props.fetchHybridKeksLabels(props.tenant.value)
    }
  }, [props.tenant])

  React.useEffect(() => {
    if (props.hybridKeksLabels.data != null && props.hybridKeksLabels.data.length > 0) {
      const uses: Option[] = []
      props.hybridKeksLabels.data.forEach((use: { toString: () => any }) => {
        uses.push({
          value: use.toString(),
          label: use.toString()
        })
      })
      props.setLabelOfHybridKekOptions(uses)
    }
  }, [props.hybridKeksLabels.data])

  const handleKeyTypeInformationWithFailure = (): JSX.Element => {
    if (props.tenantTypes.isLoading) {
      return (
        HandleError({
          isLoading: true,
          unauthorized: false,
          forbidden: false,
          noDataPresent: false
        })
      )
    } else if (props.tenantTypes.httpStatus === 401) {
      return (
        HandleError({
          isLoading: false,
          unauthorized: true,
          forbidden: false,
          noDataPresent: false
        })
      )
    } else if (props.tenantTypes.httpStatus === 403) {
      return (
        HandleError({
          isLoading: false,
          unauthorized: true,
          forbidden: false,
          noDataPresent: false
        })
      )
    } else {
      return (
        HandleError({
          isLoading: false,
          unauthorized: false,
          forbidden: false,
          noDataPresent: false,
          retryHandler: props.fetchTenants
        })
      )
    }
  }

  const handleHybridSelect = (selectedValue: Option): void => {
    props.setLabelOfHybridKek(selectedValue)
  }

  const handleTenantSelect = (selectedValue: Option): void => {
    props.setTenant(selectedValue)
  }

  const handleKeyTypeSelect = (selectedValue: Option): void => {
    props.setKeyType(selectedValue)
  }

  if (props.tenantTypes.tenants !== null && props.tenantTypes.tenants.length > 0) {
    return (
      <>
        <CardContent>
          <p id='group-info-title'>{KeyTypeMappings.EnterDetails}</p>
          <Grid container spacing={2}>
            <Grid item md={4}>
              <span style={{ fontSize: '15px', padding: 10, display: 'block' }}>{KeyTypeMappings.SelectTenant}<sup style={{ color: '#EE3426' }}>*</sup></span>
              <GenXDropdown placeholder={KeyTypeMappings.SelectTenant} value={props.tenantOptions.find(opt => opt.value === props.tenant?.value) ?? null} options={props.tenantOptions} onSelect={handleTenantSelect} />
            </Grid>
            <Grid item md={4}>
              <span style={{ fontSize: '15px', padding: 10, display: 'block' }}>{KeyTypeMappings.SelectKeyType}<sup style={{ color: '#EE3426' }}>*</sup></span>
              <KeyTypeDropdown
                placeholder={KeyTypeMappings.SelectKeyType}
                options={props.keyTypeOptions}
                value={props.keyType}
                onSelect={handleKeyTypeSelect}
                onCreateNew={(name: string) => {
                  props.setKeyTypeOptions(prev => {
                    const exists = prev.some(opt => opt.value.toLowerCase() === name.toLowerCase())
                    if (!exists) {
                      return [...prev, { value: name, label: name }]
                    }
                    return prev
                  })
                }}
                width={260}
                dataTestId={'key-type-dropdown'}
              />
            </Grid>
            <Grid item md={4}>
              <span style={{ fontSize: '15px', padding: 10, display: 'block' }}>{KeyTypeMappings.SelectHybridKey}<sup style={{ color: '#EE3426' }}>*</sup></span>
              <GenXDropdown placeholder={KeyTypeMappings.SelectHybridKey} value={props.labelOfHybridKek} options={props.labelOfHybridKekOptions} onSelect={handleHybridSelect} />
            </Grid>
          </Grid>
        </CardContent>
      </>
    )
  } else {
    return handleKeyTypeInformationWithFailure()
  }
}

interface DispatchToProps {
  fetchTenants: () => void
  fetchHybridKeksLabels: (input: string) => void
  fetchFilterTypesForKeyDeviceMappings: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchTenants: () => dispatch(fetchTenants()),
  fetchHybridKeksLabels: (input: string) => dispatch(fetchHybridKeksLabels(input)),
  fetchFilterTypesForKeyDeviceMappings: () => dispatch(fetchFilterTypesForKeyDeviceMappings())
})

interface StateToProps {
  tenantTypes: AllTenantsState
  hybridKeksLabels: HybridKeksLabelsState
  keyTypes: getFilterTypesForDeviceKeyMappingsState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  tenantTypes: state.getAllTenantsSlice,
  hybridKeksLabels: state.hybridKeksLabels,
  keyTypes: state.filterTypesForDeviceKeyMappings
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

type AllProps = PropsFromRedux & ParentProps

export default connector(KeyTypeMappingInformation)
